// Pure persistence + geometry helpers for the sessions split view, extracted
// from the hook so the storage round-trip and clamp logic are unit-testable
// without a measured DOM (jsdom can't size panels).

// Versioned so a future change to the stored semantics can't misread old
// values. Scoped per user (see getSplitStorageKey) to prevent one clinician's
// preference bleeding into another's session on a shared workstation.
const SPLIT_STORAGE_BASE_KEY = 'sessions-panel-split-px.v1';

/**
 * Default left-panel width for the side-by-side split when no preference is
 * stored. The narrow-window list sheet owns its separate 320px default.
 */
export const DEFAULT_SPLIT_PX = 400;
/**
 * Drag floor: the narrowest the list *panel* may be dragged to — also fed to the
 * page's `<ResizablePanel minSize>` so the live drag enforces it. Distinct from
 * `DEFAULT_SPLIT_PX` (the width the list *opens* at) and from
 * `SPLIT_MIN_CONTAINER_PX` (the container width at which the split engages).
 *
 * Sized so the list toolbar fits at the narrowest drag: the fixed action cluster
 * (search icon + the consolidated settings menu — refresh/filter/display-settings
 * folded into one menu in split view — + the New/Upload split CTA, ~130px) plus
 * the collapsed scope toggle at its `min-w-[5ch]` floor need ~255px of panel
 * (incl. the card's `px-4` and the ~12px inset gutter below). Below that the
 * scope toggle squeezes past its collapsed minimum and the cluster overflows the
 * card. Dropped from 350 once the toolbar was consolidated (the pre-consolidation
 * cluster held four separate icon buttons, ~200px, and floored the panel higher).
 *
 * Note: this is the floor on the panel, not the painted card. The list card sits
 * ~12px narrower than the panel because the inset wrapper adds `sm:pe-3` (12px
 * padding-end) between the panel edge and the card (see `[[...id]].tsx`), so the
 * visible list bottoms out at ~268px when inset chrome is on.
 */
export const MIN_PANEL_PX = 280;
/**
 * Minimum right-panel (session detail) width. The list may never grow wide
 * enough to push the detail pane below this — fed to the page's right
 * `<ResizablePanel minSize>` so the live drag enforces it, and folded into the
 * list's max share below so a restored/saved width respects it too.
 */
export const MIN_DETAIL_PX = 480;
/**
 * Comfortable list width required to AUTO-ENGAGE the two-pane split. The drag
 * floor (`MIN_PANEL_PX`) is lower, so once split the user may drag the list
 * narrower than this — but a single pane doesn't flip into split until the
 * container can give the list this much. Pinned independent of `MIN_PANEL_PX`
 * so lowering the drag floor never makes the split appear on narrower windows.
 */
const SPLIT_FIT_MIN_LIST_PX = 360;
/**
 * Narrowest container that can engage both panes — the list at its comfortable
 * `SPLIT_FIT_MIN_LIST_PX` plus the detail at `MIN_DETAIL_PX`. Below this the
 * layout collapses to a single pane (list-only, or detail-only when a session
 * is open) — the same behaviour as the mobile breakpoint. Kept at 840 (not
 * `MIN_PANEL_PX + MIN_DETAIL_PX`) so the engage threshold is unchanged by the
 * lower drag floor.
 */
export const SPLIT_MIN_CONTAINER_PX = SPLIT_FIT_MIN_LIST_PX + MIN_DETAIL_PX;
/**
 * Flat list ceiling for standard-width screens — max-w-xl (36rem). A session
 * list is short rows; at laptop widths, past this it's mostly whitespace.
 */
export const MAX_LIST_BASE_PX = 576;
// Above a typical laptop screen width the ceiling ramps up so wide monitors
// aren't stuck at the flat base; below the threshold the base holds, leaving
// standard screens unchanged. Slope keeps growth gentle (~1920px area → ~736px).
// See sessions-panel-split-storage.docs.md for the full recipe and rationale.
const MAX_LIST_RAMP_START_PX = 1600;
const MAX_LIST_RAMP_SLOPE = 0.5;
// Hard backstop: the list never takes more than half the container. Binds on
// smaller split screens where the flat base would otherwise exceed half.
const MAX_LIST_HARD_RATIO = 0.5;
/** Guards a value saved on a very wide monitor from exceeding a narrow screen. */
const MAX_SAVED_PX = 4000;

/**
 * Largest width (px) the list may occupy: `min(50%, 576px + 0.5·max(0, width −
 * 1600))`, further capped so the detail keeps `MIN_DETAIL_PX`. Flat at 576px up
 * to the threshold (standard laptops), then a gentle ramp on wider screens.
 *
 * On a container too narrow for both floors (< `MIN_PANEL_PX + MIN_DETAIL_PX`,
 * reachable when a width saved on a wider monitor is restored) the detail-derived
 * cap falls below the list floor; floor the result at `MIN_PANEL_PX` so the list
 * keeps its minimum and the detail absorbs the shortfall.
 */
export const computeMaxSplitPx = (containerWidth: number): number => {
  const ramped =
    MAX_LIST_BASE_PX +
    MAX_LIST_RAMP_SLOPE * Math.max(0, containerWidth - MAX_LIST_RAMP_START_PX);
  const softCap = Math.min(ramped, containerWidth * MAX_LIST_HARD_RATIO);
  const detailCapped = containerWidth - MIN_DETAIL_PX;
  return Math.max(MIN_PANEL_PX, Math.min(softCap, detailCapped));
};

/** `computeMaxSplitPx` as a percentage of the container, for the % clamp path. */
export const computeMaxSplitPercent = (containerWidth: number): number =>
  (computeMaxSplitPx(containerWidth) / containerWidth) * 100;

/**
 * Clamp a target list width to `[MIN_PANEL_PX, computeMaxSplitPx]`. Applied on
 * save so a collapse-drag (which momentarily reports a near-fullscreen width)
 * can't persist a width that later squeezes the detail below its min.
 */
export const clampSplitPx = (containerWidth: number, px: number): number =>
  Math.min(Math.max(px, MIN_PANEL_PX), computeMaxSplitPx(containerWidth));

/**
 * Per-user storage key. `'anon'` covers the brief window before auth resolves;
 * because a width is only ever saved once a real id is known, the `anon` key is
 * never written — there is nothing to migrate when the id arrives.
 */
export const getSplitStorageKey = (userId?: string | null): string =>
  `${SPLIT_STORAGE_BASE_KEY}:${userId ?? 'anon'}`;

export const readSavedSplitPx = (key: string): number | null => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const px = parseFloat(raw);
    return Number.isFinite(px) && px > 0 && px < MAX_SAVED_PX ? px : null;
  } catch {
    return null;
  }
};

export const saveSplitPx = (key: string, px: number): void => {
  try {
    localStorage.setItem(key, Math.round(px).toString());
  } catch {
    // localStorage unavailable (SSR, private mode quota)
  }
};

/**
 * Convert a stored/target pixel width to a clamped split percentage for the
 * given container width. Returns `null` when the container is unmeasured so the
 * caller defers applying a width rather than guessing. The lower bound is
 * derived from `MIN_PANEL_PX` so the percentage clamp can never drift from the
 * pixel `minSize` the resizable library enforces; the upper bound comes from
 * `computeMaxSplitPercent`, keeping `MIN_DETAIL_PX` for the detail pane.
 */
export const computeSplitPercent = (
  containerWidth: number,
  targetPx: number,
  savedPx: number | null
): number | null => {
  if (!containerWidth) return null;
  const target = savedPx ?? targetPx;
  const minPercent = (MIN_PANEL_PX / containerWidth) * 100;
  return Math.min(
    Math.max((target / containerWidth) * 100, minPercent),
    computeMaxSplitPercent(containerWidth)
  );
};
